import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { AUTH } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  // Deep-link support: the confirmation email sends attendees to
  // /login?tab=attendee&code=TLSUMMIT2026 for one-click sign-in.
  const initialTab = params.get("tab") === "attendee" ? "attendee" : "exhibitor";
  const initialCode = params.get("code") || "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [showSupplierLink, setShowSupplierLink] = useState(false);

  const [supplierEmail, setSupplierEmail] = useState("");
  const [supplierSending, setSupplierSending] = useState(false);

  const [code, setCode] = useState(initialCode);
  const [aName, setAName] = useState("");
  const [aEmail, setAEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // If the URL carries a code, keep it in sync (handles browser back/forward).
  useEffect(() => {
    if (initialCode && !code) setCode(initialCode);
  }, [initialCode, code]);

  const passwordSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const { data } = await api.post("/auth/password-login", { email, password });
      login(data.token, data.user);
      toast.success("Signed in.");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Sign-in failed. Check your email and password.";
      toast.error(msg);
    } finally {
      setSigningIn(false);
    }
  };

  const supplierMagicLink = async (e) => {
    e.preventDefault();
    setSupplierSending(true);
    try {
      await api.post("/auth/request-magic-link", { email: supplierEmail });
      toast.success(`Sign-in link sent to ${supplierEmail}. Check your email.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send sign-in link.");
    } finally {
      setSupplierSending(false);
    }
  };

  const attendeeSubmit = async (e) => {
    e.preventDefault();
    if (!aEmail.trim()) {
      toast.error("Please enter your registered email so we can link your account and schedule.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { code, email: aEmail.trim() };
      if (aName) payload.name = aName;
      const { data } = await api.post("/auth/access-code", payload);
      login(data.token, data.user);
      toast.success("Welcome to the Summit Hub.");
      nav("/documents");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid access code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="container-summit py-16 md:py-24 max-w-2xl">
        <div className="overline mb-4"><span className="accent-rule" />Access</div>
        <h1 className="text-4xl sm:text-5xl mb-3">Sign in</h1>
        <p className="text-zinc-500 mb-10">
          Prime Suppliers and Display America admins sign in under <strong>Organizer / Exhibitor</strong>. SBEs, Travel + Leisure Team, Speakers, and Guests sign in under <strong>SBE / Attendee</strong> using the event access code and their registered email.
        </p>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full h-12 bg-zinc-100 rounded-none">
            <TabsTrigger value="exhibitor" data-testid={AUTH.exhibitorTab} className="rounded-none uppercase tracking-[0.18em] text-xs">
              Organizer / Exhibitor
            </TabsTrigger>
            <TabsTrigger value="attendee" data-testid={AUTH.attendeeTab} className="rounded-none uppercase tracking-[0.18em] text-xs">
              SBE / Attendee
            </TabsTrigger>
          </TabsList>

          <TabsContent value="exhibitor" className="pt-8">
            <form onSubmit={passwordSignIn} className="space-y-6" data-testid="admin-password-form">
              <div>
                <Label className="overline">Email address</Label>
                <Input
                  data-testid={AUTH.emailInput}
                  type="email"
                  required
                  placeholder="you@displayamerica.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 mt-3 rounded-none focus-visible:ring-1 focus-visible:ring-black"
                />
              </div>
              <div>
                <Label className="overline">Password</Label>
                <Input
                  data-testid="admin-password-input"
                  type="password"
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 mt-3 rounded-none focus-visible:ring-1 focus-visible:ring-black"
                />
              </div>
              <button data-testid="admin-signin-btn" disabled={signingIn} className="btn-black w-full">
                <Lock size={16} /> {signingIn ? "Signing in…" : "Sign In"}
              </button>
              <div className="pt-4 border-t border-zinc-200">
                <button
                  type="button"
                  onClick={() => setShowSupplierLink((v) => !v)}
                  className="text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-black"
                  data-testid="toggle-supplier-signin">
                  Prime Supplier? Get a sign-in link →
                </button>
                {showSupplierLink && (
                  <div className="mt-5 space-y-4">
                    <Label className="overline">Prime Supplier email</Label>
                    <Input
                      data-testid="supplier-magic-email"
                      type="email"
                      placeholder="you@yourcompany.com"
                      value={supplierEmail}
                      onChange={(e) => setSupplierEmail(e.target.value)}
                      className="h-12 mt-3 rounded-none focus-visible:ring-1 focus-visible:ring-black"
                    />
                    <button
                      type="button"
                      onClick={supplierMagicLink}
                      disabled={supplierSending || !supplierEmail}
                      data-testid="supplier-magic-send"
                      className="btn-outline-black w-full h-12">
                      <Mail size={16} /> {supplierSending ? "Sending…" : "Email me a sign-in link"}
                    </button>
                    <p className="text-xs text-zinc-500">
                      We&apos;ll email a one-tap sign-in link to the address associated with your Prime Supplier registration.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </TabsContent>

          <TabsContent value="attendee" className="pt-8">
            <div className="mb-6 border border-zinc-200 bg-zinc-50 px-4 py-3" data-testid="attendee-guidance">
              <p className="text-sm text-zinc-700 leading-relaxed">
                <strong>SBEs, Travel + Leisure Team, Speakers, and Guests</strong> sign in here. Enter the event <strong>access code</strong> and your <strong>registered email</strong> — your email links you to your registration and, if you have one, your Business Matchmaking schedule.
              </p>
            </div>
            <form onSubmit={attendeeSubmit} className="space-y-6">
              <div>
                <Label className="overline">Access Code</Label>
                <Input
                  data-testid={AUTH.accessCodeInput}
                  required
                  placeholder="Enter event access code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="h-12 mt-3 rounded-none focus-visible:ring-1 focus-visible:ring-black tracking-widest uppercase"
                />
              </div>
              <div>
                <Label className="overline">Registered Email</Label>
                <Input
                  data-testid={AUTH.attendeeEmailInput}
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={aEmail}
                  onChange={(e) => setAEmail(e.target.value)}
                  className="h-12 mt-3 rounded-none focus-visible:ring-1 focus-visible:ring-black"
                />
                <p className="text-xs text-zinc-500 mt-2">
                  Use the same email you registered with so we can show your schedule and personalize your portal.
                </p>
              </div>
              <div>
                <Label className="overline">Name (optional)</Label>
                <Input
                  data-testid={AUTH.attendeeNameInput}
                  placeholder="Your full name"
                  value={aName}
                  onChange={(e) => setAName(e.target.value)}
                  className="h-12 mt-3 rounded-none focus-visible:ring-1 focus-visible:ring-black"
                />
              </div>
              <button data-testid={AUTH.attendeeSubmit} disabled={submitting} className="btn-black w-full">
                <KeyRound size={16} /> {submitting ? "Verifying…" : "Enter Summit Hub"}
              </button>
              <p className="text-xs text-zinc-500">
                Your access code was included in your registration confirmation email from
                <a href="mailto:info@displayamerica.com" className="underline underline-offset-4 ml-1">info@displayamerica.com</a>.
                Can&apos;t find it? Contact Display America.
              </p>
              <p className="text-xs text-zinc-500">
                Tip: scan your invitation QR code on a mobile device to be signed in automatically.
              </p>
            </form>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}
